interface PendingTransactionDetails {
    untriedCount: number;
    failedCount: number;
}

interface WeeklyStatusSummary {
    successCount: number;
    failureCount: number;
}

interface FileStats {
    count: number;
    totalTransactions: number;
}

export interface TransactionFilter {
    status?: string;
    pbmName?: string;
    uaid?: string;
    dateFilledStart?: string;
    dateFilledEnd?: string;
    dateReceivedByNASHStart?: string;
    dateReceivedByNASHEnd?: string;
    organizationId?: number;
}

export interface Transaction {
    id: number;
    pbmId: number;
    pbmName?: string;
    pharmNabp: string;
    rxNum: string;
    dateFilled: string;
    groupId: string;
    cardHolderId: string;
    submittedPc: string;
    dateOfBirth?: string;
    genderCode: string;
    patientFirstName: string;
    patientLastName: string;
    patientMiddleName?: string;
    refillCode?: string;
    daysSupply?: number;
    ndc?: string;
    ingrCostSubmitted?: number;
    prescriberId: string;
    dateWritten?: string;
    numRefillsAuthorized?: number;
    unc?: string;
    qtyDispensed?: number;
    claimMessage: string;
    dateEnteredByPharmacy?: string;
    dateReceivedByNASH: string;
    fileOutputId?: number;
    fileOutputStatus?: string;
    fileOutputLocation?: string;
    dateLogCreatedBySystem?: string;
    uaid?: string;
    inputFileLogId?: number;
    pending: boolean;
    claimId?: string;
    otherId?: string;
    nashPatientId?: string;
    nashReferralOrg?: string;
    drugDispensed?: string;
    drugStrength?: string;
    priorAuthApprovalDate?: string;
    priorAuthExpirationDate?: string;
    travelCost?: number;
    planPay?: number;
}

import apiClient from '../utils/apiClient';

export const getTransactions = async (filter?: TransactionFilter): Promise<Transaction[]> => {
    let url = 'api/Transaction';
    
    if (filter) {
        const params = new URLSearchParams();
        if (filter.status) params.append('status', filter.status);
        if (filter.pbmName) params.append('pbmName', filter.pbmName);
        if (filter.uaid) params.append('uaid', filter.uaid);
        if (filter.dateFilledStart) params.append('dateFilledStart', filter.dateFilledStart);
        if (filter.dateFilledEnd) params.append('dateFilledEnd', filter.dateFilledEnd);
        if (filter.dateReceivedByNASHStart) params.append('dateReceivedByNASHStart', filter.dateReceivedByNASHStart);
        if (filter.dateReceivedByNASHEnd) params.append('dateReceivedByNASHEnd', filter.dateReceivedByNASHEnd);
        if (filter.organizationId) params.append('organizationId', filter.organizationId.toString());
        
        if (params.toString()) {
            url += `?${params.toString()}`;
        }
    }

    return apiClient.get<Transaction[]>(url);
};

export const getPendingTransactionDetails = async (): Promise<PendingTransactionDetails> => {
    return apiClient.get<PendingTransactionDetails>('api/Transaction/pending-details');
};

export const getWeeklyTransactionStatusSummary = async (): Promise<WeeklyStatusSummary> => {
    return apiClient.get<WeeklyStatusSummary>('api/Transaction/weekly-status-summary');
};

export const getWeeklyFileStatusSummary = async (): Promise<WeeklyStatusSummary> => {
    return apiClient.get<WeeklyStatusSummary>('api/FileOutputLog/stats');
};

interface ProcessTransactionResponse {
    message: string;
    processedFiles: number;
    successfulFiles: number;
    failedFiles: number;
    errors: string[];
    results?: FileOutputLogModel[];
}

interface FileOutputLogModel {
    id: number;
    location: string;
    pbmConnectionId: number;
    status: string;
    dateCreatedBySystem: string;
    uaid: string;
}

export const processTransactions = async (): Promise<ProcessTransactionResponse> => {
    try {
        return await apiClient.post<ProcessTransactionResponse>('api/transaction/process');
    } catch (error: any) {
        console.error('[processTransaction]', error);
        const enhancedError = new Error(error.message || 'Failed to process transactions') as Error & {
            successfulFiles?: number;
            failedFiles?: number;
        };
        enhancedError.successfulFiles = error.successfulFiles;
        enhancedError.failedFiles = error.failedFiles;
        throw enhancedError;
    }
};

export const uploadTransactionFile = async (file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);
    await apiClient.post('api/transaction/upload', formData);
};

export const getDailyFileStats = async (): Promise<FileStats> => {
    return apiClient.get<FileStats>('api/files/daily-stats');
};

export const getWeeklyFileStats = async (): Promise<FileStats> => {
    return apiClient.get<FileStats>('api/files/weekly-stats');
};

export const togglePendingStatus = async (id: number, newPendingStatus: boolean): Promise<void> => {
    await apiClient.patch(`api/transaction/${id}/pending`, newPendingStatus);
};
